<template>
  <div class="app-header-component">
    <router-link v-if="!isMobileOrTabletScreen" class="voodoo-logo" :to="gamesListLink" data-cy="voodootune-logo">
      <VoodooTuneLogo />
    </router-link>
    <vd-custom-icon
      v-if="isMobileOrTabletScreen && currentRouteName !== gamesListLink.name"
      name="burger-menu-icon"
      class="burger-menu-icon"
      big
      @click="toggleMobileMenu"
    />
    <el-drawer
      v-if="isMobileOrTabletScreen && currentRouteName !== gamesListLink.name"
      :model-value="showMobileMenu"
      direction="ltr"
      modal
      size="250px"
    >
      <template #title>
        <router-link class="voodoo-logo" :to="gamesListLink">
          <VoodooTuneLogo />
        </router-link>
      </template>
      <GameMenu @close="toggleMobileMenu" />
    </el-drawer>
    <div class="content">
      <div id="teleport-app-header-content" />

      <section class="right-section">
        <NotificationMenuItem />
        <vd-dropdown class="questions-menu">
          <template #head>
            <VdIcon store="fas" name="circle-question" size="large" />
          </template>

          <vd-dropdown-item disabled>VoodooTune v{{ voodooTuneVersion }}</vd-dropdown-item>
          <vd-dropdown-item @click="openGlobalDocumentation()">Open Documentation</vd-dropdown-item>
        </vd-dropdown>

        <vd-dropdown v-if="user" class="user-menu">
          <template #head>
            <VdIcon store="fas" name="user" class="user-icon" />
            <VdIcon store="fa" name="chevron-down" class="user-chevron-icon" />
          </template>

          <vd-dropdown-item disabled>{{ user && user.name }}</vd-dropdown-item>

          <vd-dropdown-item @click="logout">Logout</vd-dropdown-item>
        </vd-dropdown>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * This component is the header of our App.
 * It displays the VoodooTune logo, the user dropdown menu if it is authenticated,
 * and a portal that can be override to add game information.
 * The VoodooTune logo is clickable and redirect to the games list page.
 */
import { computed, defineComponent } from "vue";
import { useRoute } from "vue-router";

import NotificationMenuItem from "./NotificationMenuItem/NotificationMenuItem.vue";

import VoodooTuneLogo from "@/assets/VoodooTuneLogo.vue";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import config from "@/config";
import { GLOBAL_DOCUMENTATION_LINK } from "@/constants";
import useScreenSizes from "@/hooks/screen-sizes";
import useToggle from "@/hooks/toggle";
import useAuthentication from "@/hooks/use-authentication";
import useUserTracking from "@/hooks/user-tracking";
import GameMenu from "@/pages/games/GameDisplay/components/GameMenu.vue";
import links from "@/router/links";

export default defineComponent({
  name: "AppHeader",

  components: {
    VoodooTuneLogo,
    GameMenu,
    NotificationMenuItem,
    VdIcon,
  },

  setup() {
    const route = useRoute();

    /**
     * Get auth stuff
     */
    const { user, logout } = useAuthentication();
    useUserTracking(user);

    const { isMobileOrTabletScreen } = useScreenSizes();
    const { state: showMobileMenu, toggle: toggleMobileMenu } = useToggle(false);

    const currentRouteName = computed(() => route.name);

    /**
     * Some links
     */
    const gamesListLink = { name: links.games.list };

    /**
     * Opens the main documentation link
     */
    const openGlobalDocumentation = () => {
      window.open(GLOBAL_DOCUMENTATION_LINK, "_blank");
    };

    return {
      voodooTuneVersion: config.version,
      user,
      logout,
      gamesListLink,
      currentRouteName,
      isMobileOrTabletScreen,
      showMobileMenu,
      toggleMobileMenu,
      openGlobalDocumentation,
    };
  },
});
</script>

<style lang="scss" scoped>
.app-header-component {
  margin: 0 auto;
  padding: size("L");
  padding-bottom: 0;

  display: flex;
  align-items: center;

  .burger-menu-icon {
    cursor: pointer;
    margin-right: size("ML");
  }

  .voodoo-logo {
    width: 170px;
  }

  .content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .right-section {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;

    > div:not(:last-child) {
      margin-right: size("ML");
    }

    .questions-menu {
      margin-right: size("M");
    }

    .vd-icon {
      font-size: 20px;
    }
    .user-menu {
      .vd-icon {
        margin-right: size("XXS");

        &.user-chevron-icon {
          font-size: 14px;
        }

        &.user-icon {
          margin-right: 10px;
        }
      }
    }
  }

  @include small-screen {
    .user-menu {
      min-width: 40px;

      .user-name {
        display: none;
      }
    }
  }

  @include wide-screen {
    width: calc(100% - 60px);
    max-width: 2000px;
  }
}
</style>

<style lang="scss">
/**
*  Applied on tablets and mobile screens
*/
@include medium-screen {
  .voodoo-logo {
    width: 100px;
  }
}
</style>
